import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrentProductService } from '@spartacus/storefront';
import { CmsService } from '@spartacus/core';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { IDocument, SdDocumentsComponent } from '@seb/web-design-system/documents';
import {
  BrandsEnum,
  BrandThemesEnum, IComponentWrapper,
  ImageRatioEnum,
  IPushCard, IPushCardWithLayout,
  PositionEnum, PushSizeEnum, SdComponentWrapperComponent, SdPushCardComponent, SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { LameConfig, PushCardComponentInterface } from '@seb/web-common/core/models';
import {
  BrandCornerMapper,
  DocumentMapper,
  HeadingParagraphMapper,
  PushCardMapper
} from '@seb/web-common/core/mappers';
import { TrackingService, UrlMappingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-ifu-list',
  templateUrl: './ifu-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdComponentWrapperComponent, SdDocumentsComponent, SdPushCardComponent]
})
export class IfuListComponent implements AfterViewInit, OnDestroy {
  headingTitleThemesConfig = BrandThemesEnum.brand;
  pushSizeEnum = PushSizeEnum;
  documents: IDocument[] = [];
  productName: string;
  pushCardTextConfig = PositionEnum.center;
  pushCardMediaConfig = PositionEnum.left;
  pushCardImageRatio = ImageRatioEnum.r4_3;
  pushCardThemeConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.promo;
  pushCardMediaAsBackground = false;
  pushCardData: IPushCard;
  wrapperContent: IComponentWrapper;
  lameConfig: LameConfig;
  push: TemplateRef<SdPushCardComponent>;

  @Input() brand = BrandsEnum.default;
  @Input() pageTemplate = '';

  @ViewChild('pushTemplate')
  pushCardTemplate: TemplateRef<SdPushCardComponent>;

  @Output() hasLoaded = new EventEmitter<void>();

  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private currentProductService: CurrentProductService,
    private documentMapper: DocumentMapper,
    private cmsService: CmsService,
    private pushCardMapper: PushCardMapper,
    private translateService: TranslateService,
    private trackingService: TrackingService,
    private urlMappingService: UrlMappingService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private brandCornerMapper: BrandCornerMapper
  ) {}

  ngAfterViewInit(): void {
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.ifu;
    const richParagraphData = {
      heading: {
        headline: this.translateService.instant('pdp.sectionTitles.documents'),
        headlineLevel: 'H2'
      },
      titleTheme: this.lameConfig?.titleTheme || BrandThemesEnum.brand,
      titleLevel: 2,
      size: SizesEnum.l,
      isUnderlined: this.lameConfig?.titleUnderlined
    };
    this.wrapperContent = {
      links: [],
      footNotes: [],
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
    };
    this.cmsService
      .getContentSlot('DocumentsPushPromo')
      .pipe(
        switchMap((component) =>
          !!component.components && component.components.length > 0
            ? this.cmsService.getComponentData(component.components[0].uid)
            : of(null)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((componentData: PushCardComponentInterface) => {
        if (componentData) {
          const pushCardWithLayout: IPushCardWithLayout = this.pushCardMapper.toPushCardWithLayout(componentData);
          this.pushCardTextConfig = pushCardWithLayout.textConfig;
          this.pushCardMediaConfig = pushCardWithLayout.mediaConfig;
          this.pushCardImageRatio = pushCardWithLayout.imageRatio;
          this.pushCardThemeConfig = pushCardWithLayout.themesConfig;
          this.pushCardMediaAsBackground = pushCardWithLayout.mediaAsBackground;
          this.pushCardData = pushCardWithLayout.push;
          this.push = this.pushCardTemplate;
        } else {
          this.pushCardData = undefined;
          this.push = undefined;
        }
        this.cdRef.markForCheck();
      });

    const product$ = this.currentProductService.getProduct();
    const warrantyLink$ = this.urlMappingService.getUrlMappingByCode(
      this.brandCornerMapper.toWcConfig(this.brand)?.warranty || '/warranty-&-repair'
    );

    combineLatest([product$, warrantyLink$])
      .pipe(
        filter(([product]) => !!product),
        takeUntil(this.destroy$)
      )
      .subscribe(([product, warrantyLink]) => {
        if (product.instructionsForUse?.instructionsForUse) {
          this.documents = this.documentMapper.getInstructionsForUseToDocument(
            product.instructionsForUse.instructionsForUse
          );
        }
        if (warrantyLink) {
          const warranty: IDocument = {
            link: warrantyLink.value,
            title: this.translateService.instant('ifuTitles.WARRANTY'),
            isLink: true
          };
          this.documents.push(warranty);
        }

        this.productName = product.name;
        this.hasLoaded.emit();
        this.cdRef.markForCheck();
      });
  }

  onClick(document: IDocument): void {
    this.trackingService.sendEvent('documentationDownload', {
      productName: this.productName,
      documentationName: document.title
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
